import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { User } from "src/app/entities/users";
import { AuthService } from "src/app/services/auth.service";
import { NodeService } from "src/app/services/node.service";
import { PwaInstallService } from "src/app/services/pwa-install.service";
import { GlobalService } from "src/app/shared/global.service";
import { LoginSubmitPayload } from "src/app/shared/components/login-shell/login-shell.component";
import Base64 from "crypto-js/enc-base64";
import Utf8 from "crypto-js/enc-utf8";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit, OnDestroy {
  loading = false;
  loginError: string | null = null;
  startInRecover = false;
  recoverSuccess = false;
  recoverError: string | null = null;

  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private destroy$ = new Subject<void>();

  // ######################################
  // FUNCTION: constructor
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private g: GlobalService,
    private translate: TranslateService,
    private node: NodeService,
    private pwaInstall: PwaInstallService,
  ) { }

  // ######################################
  // FUNCTION: ngOnInit
  ngOnInit() {
    this.loginError = null;
    this.startInRecover = !!this.route.snapshot.data['recover'];
    // La selezione del manifest admin/cliente avviene nello <script> inline
    // di index.html PRIMA del bootstrap, cosi' Chrome valuta il manifest
    // giusto al primo parse del DOM e il prompt di installazione appare
    // nel menu ⋮. La chiamata qui sotto copre solo i soft-navigate SPA.
    this.pwaInstall.setManifest("admin");
  }

  ngOnDestroy(): void {
    this.clearCountdown();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ######################################
  // FUNCTION: ngAfterViewInit
  ngAfterViewInit() {
    setTimeout(() => {
      this.g.actualPage = "";
    });
  }

  // ######################################
  // FUNCTION: onShellSubmit
  // Called by <app-login-shell> when the user submits the form.
  // Preserves the exact payload contract with /users/login - only adds an
  // optional `captcha` field (backend ignores it during soft-fail rollout).
  onShellSubmit(payload: LoginSubmitPayload): void {
    if (!payload.user || !payload.pwd) {
      this.loginError = this.translate.instant("wrongLogin");
      return;
    }
    this.loading = true;
    this.loginError = null;

    const crypt: string = Base64.stringify(
      Utf8.parse(payload.pwd)
    );
    const formData: any = {
      pwd: crypt,
      user: payload.user,
      captcha: payload.captchaToken,
    };

    this.auth.checkLogin(formData).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.g.loggedInUser = res.user as User;
        sessionStorage.setItem('id', res.user.id);
        this.auth.saveLoginResponse(res);
        this.router.navigateByUrl("/admin");
      },
      error: (err: { detail?: string | { message?: string; retryAfter?: number }; status?: number }) => {
        if (err?.status === 429) {
          const detail = err.detail;
          const retryAfter = typeof detail === "object" ? detail?.retryAfter : null;
          if (retryAfter && retryAfter > 0) {
            this.startCountdown(retryAfter);
          } else {
            this.loginError = typeof detail === "string"
              ? detail
              : detail?.message || "Troppi tentativi. Riprova più tardi.";
          }
        } else {
          this.loginError = this.translate.instant("wrongLogin");
        }
        this.loading = false;
      }
    });
  }

  private startCountdown(seconds: number): void {
    this.clearCountdown();
    let remaining = seconds;
    this.loginError = this.formatCountdown(remaining);

    this.countdownTimer = setInterval(() => {
      remaining--;
      if (remaining <= 0) {
        this.clearCountdown();
        this.loginError = null;
      } else {
        this.loginError = this.formatCountdown(remaining);
      }
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private formatCountdown(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const time = mins > 0
      ? `${mins}:${secs.toString().padStart(2, "0")}`
      : `${secs}s`;
    return `Troppi tentativi. Riprova tra ${time}`;
  }

  // ######################################
  // FUNCTION: onShellRecover
  onShellRecover(email: string): void {
    this.recoverSuccess = false;
    this.recoverError = null;
    const formData = { user: email };
    this.node.recoverUser(formData).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.recoverSuccess = true;
      },
      error: (err: { detail?: string; status?: number }) => {
        this.recoverError = typeof err?.detail === "string"
          ? err.detail
          : "L'indirizzo inserito non esiste o non dispone degli accessi al sistema.";
      },
    });
  }
}
